import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL;

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handle = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handle);
    return () => window.removeEventListener('resize', handle);
  }, []);
  return isMobile;
};

const makeStyles = (isMobile) => ({
  page: { minHeight: '100vh', background: '#0a0a0a', paddingTop: isMobile ? '88px' : '100px', paddingBottom: '80px', fontFamily: 'Jost, sans-serif' },
  container: { maxWidth: '1100px', margin: '0 auto', padding: isMobile ? '0 16px' : '0 32px' },
  eyebrow: { fontSize: '0.62rem', letterSpacing: '0.4em', textTransform: 'uppercase', color: '#c9a96e', marginBottom: '8px' },
  pageTitle: { fontFamily: 'Cormorant Garamond, serif', fontSize: isMobile ? '2rem' : '2.8rem', fontWeight: 300, color: '#f5f0eb', marginBottom: '32px', lineHeight: 1 },
  tabs: { display: 'flex', borderBottom: '1px solid #1e1e1e', marginBottom: '40px', overflowX: 'auto' },
  tab: (active) => ({ padding: isMobile ? '12px 16px' : '12px 28px', fontSize: '0.62rem', letterSpacing: '0.3em', textTransform: 'uppercase', color: active ? '#c9a96e' : '#3a3a3a', cursor: 'pointer', border: 'none', background: 'none', fontFamily: 'Jost, sans-serif', borderBottom: active ? '2px solid #c9a96e' : '2px solid transparent', marginBottom: '-1px', whiteSpace: 'nowrap' }),
  statsGrid: { display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: '1px', background: '#1a1a1a', marginBottom: '1px' },
  statCard: { background: '#0f0f0f', padding: '28px 20px', textAlign: 'center' },
  statVal: { fontFamily: 'Cormorant Garamond, serif', fontSize: '2.4rem', fontWeight: 300, color: '#c9a96e', lineHeight: 1, marginBottom: '8px' },
  statLabel: { fontSize: '0.58rem', letterSpacing: '0.22em', textTransform: 'uppercase', color: '#3a3a3a' },
  searchRow: { display: 'flex', gap: '12px', marginBottom: '20px', alignItems: 'center', flexWrap: 'wrap' },
  input: { background: '#0f0f0f', border: '1px solid #2a2a2a', padding: '12px 16px', color: '#f5f0eb', fontFamily: 'Jost, sans-serif', fontSize: '0.82rem', outline: 'none', flex: 1, minWidth: '200px' },
  btn: { background: '#c9a96e', border: 'none', padding: '12px 24px', color: '#0a0a0a', fontFamily: 'Jost, sans-serif', fontSize: '0.65rem', letterSpacing: '0.2em', textTransform: 'uppercase', cursor: 'pointer' },
  outlineBtn: { background: 'transparent', border: '1px solid #3a3a3a', padding: '10px 20px', color: '#a09890', fontFamily: 'Jost, sans-serif', fontSize: '0.62rem', letterSpacing: '0.2em', textTransform: 'uppercase', cursor: 'pointer' },
  dangerBtn: { background: 'transparent', border: '1px solid #7a3030', padding: '10px 20px', color: '#e07070', fontFamily: 'Jost, sans-serif', fontSize: '0.62rem', letterSpacing: '0.2em', textTransform: 'uppercase', cursor: 'pointer' },
  approveBtn: { background: 'transparent', border: '1px solid #4a7a4a', padding: '10px 20px', color: '#7aad7a', fontFamily: 'Jost, sans-serif', fontSize: '0.62rem', letterSpacing: '0.2em', textTransform: 'uppercase', cursor: 'pointer' },
  table: { width: '100%', borderCollapse: 'collapse', border: '1px solid #1a1a1a' },
  th: { padding: '12px 16px', fontSize: '0.55rem', letterSpacing: '0.25em', textTransform: 'uppercase', color: '#3a3a3a', textAlign: 'left', borderBottom: '1px solid #1a1a1a', background: '#0f0f0f' },
  td: { padding: '14px 16px', fontSize: '0.78rem', color: '#a09890', borderBottom: '1px solid #111111', fontWeight: 300 },
  tdName: { padding: '14px 16px', fontSize: '0.82rem', color: '#f5f0eb', borderBottom: '1px solid #111111', fontWeight: 300 },
  formGrid: { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '12px', marginBottom: '20px' },
  formGroup: { display: 'flex', flexDirection: 'column', gap: '6px' },
  formLabel: { fontSize: '0.58rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: '#3a3a3a' },
  formInput: { background: '#0f0f0f', border: '1px solid #2a2a2a', padding: '12px 16px', color: '#f5f0eb', fontFamily: 'Jost, sans-serif', fontSize: '0.82rem', outline: 'none', width: '100%', boxSizing: 'border-box' },
  formSelect: { background: '#0f0f0f', border: '1px solid #2a2a2a', padding: '12px 16px', color: '#f5f0eb', fontFamily: 'Jost, sans-serif', fontSize: '0.82rem', outline: 'none', width: '100%' },
  formPanel: { background: '#0f0f0f', border: '1px solid #2a2a2a', padding: '28px', marginBottom: '24px' },
  formTitle: { fontFamily: 'Cormorant Garamond, serif', fontSize: '1.4rem', fontWeight: 300, color: '#f5f0eb', marginBottom: '24px' },
  successMsg: { color: '#7aad7a', fontSize: '0.72rem', letterSpacing: '0.1em', marginBottom: '16px' },
  errorMsg: { color: '#e07070', fontSize: '0.72rem', letterSpacing: '0.1em', marginBottom: '16px' },
  badge: (color) => ({ display: 'inline-block', fontSize: '0.52rem', letterSpacing: '0.2em', textTransform: 'uppercase', color, border: `1px solid ${color}33`, padding: '2px 7px' }),
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' },
  modal: { background: '#0f0f0f', border: '1px solid #2a2a2a', padding: '40px', maxWidth: '400px', width: '100%', textAlign: 'center' },
  modalTitle: { fontFamily: 'Cormorant Garamond, serif', fontSize: '1.4rem', fontWeight: 300, color: '#f5f0eb', marginBottom: '16px' },
  modalMsg: { fontSize: '0.8rem', color: '#a09890', marginBottom: '32px', lineHeight: 1.6 },
  modalBtns: { display: 'flex', gap: '12px', justifyContent: 'center' },
});

const Admin = ({ user, token }) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const s = makeStyles(isMobile);

  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [fragrances, setFragrances] = useState([]);
  const [fragSearch, setFragSearch] = useState('');
  const [fragLoading, setFragLoading] = useState(false);
  const [editingFrag, setEditingFrag] = useState(null);
  const [showAddFrag, setShowAddFrag] = useState(false);
  const [fragForm, setFragForm] = useState({});
  const [fragMsg, setFragMsg] = useState('');
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [userLoading, setUserLoading] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userMsg, setUserMsg] = useState('');
  const [entries, setEntries] = useState([]);
  const [entryLoading, setEntryLoading] = useState(false);
  const [entryMsg, setEntryMsg] = useState('');
  const [confirmModal, setConfirmModal] = useState(null);
  const [unverified, setUnverified] = useState([]);
  const [unverifiedLoading, setUnverifiedLoading] = useState(false);
  const [unverifiedMsg, setUnverifiedMsg] = useState('');
  const [editingUnverified, setEditingUnverified] = useState(null);
  const [unverifiedForm, setUnverifiedForm] = useState({});

  // Submissions state
  const [submissions, setSubmissions] = useState([]);
  const [subLoading, setSubLoading] = useState(false);
  const [subMsg, setSubMsg] = useState('');
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [editingSubmission, setEditingSubmission] = useState(null);
  const [subForm, setSubForm] = useState({});

  const headers = { Authorization: `Bearer ${token}` };
  const fragSearchTimer = useRef(null);
  const userSearchTimer = useRef(null);

  useEffect(() => {
    if (user && !user.is_admin) navigate('/');
    if (!user) navigate('/');
  }, [user, navigate]);

  useEffect(() => {
    if (user?.is_admin) fetchStats();
  }, [user]); // eslint-disable-line

  useEffect(() => {
    if (activeTab === 'fragrances') fetchFragrances();
    if (activeTab === 'users') fetchUsers();
    if (activeTab === 'journal') fetchEntries();
    if (activeTab === 'unverified') fetchUnverified();
    if (activeTab === 'submissions') fetchSubmissions();
  }, [activeTab]); // eslint-disable-line

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/stats`, { headers });
      setStats(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchFragrances = async (search = fragSearch) => {
    setFragLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/fragrances?search=${encodeURIComponent(search)}&verified=true`, { headers });
      setFragrances(res.data);
    } catch (err) { console.error(err); }
    setFragLoading(false);
  };

  const fetchUsers = async (search = userSearch) => {
    setUserLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/users?search=${encodeURIComponent(search)}`, { headers });
      setUsers(res.data);
    } catch (err) { console.error(err); }
    setUserLoading(false);
  };

  const fetchEntries = async () => {
    setEntryLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/journal`, { headers });
      setEntries(res.data);
    } catch (err) { console.error(err); }
    setEntryLoading(false);
  };

  const fetchUnverified = async () => {
    setUnverifiedLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/fragrances?verified=false&limit=200`, { headers });
      setUnverified(res.data);
    } catch (err) { console.error(err); }
    setUnverifiedLoading(false);
  };

  const fetchSubmissions = async () => {
    setSubLoading(true);
    try {
      const res = await axios.get(`${API}/api/submissions`, { headers });
      setSubmissions(res.data);
    } catch (err) { console.error(err); }
    setSubLoading(false);
  };

  const openEditSubmission = (sub) => {
    setEditingSubmission(sub);
    const parseNotes = (n) => { if (!n) return ''; if (Array.isArray(n)) return n.join(', '); try { return JSON.parse(n).join(', '); } catch { return n; } };
    setSubForm({
      name: sub.name || '',
      brand: sub.brand || '',
      type: sub.type || '',
      gender: sub.gender || '',
      main_accord: sub.main_accord || '',
      price_gbp: sub.price_gbp || '',
      year_released: sub.year_released || '',
      longevity: sub.longevity || '',
      sillage: sub.sillage || '',
      season: Array.isArray(sub.season) ? sub.season.join(', ') : (sub.season || ''),
      occasion: sub.occasion || '',
      top_notes: parseNotes(sub.top_notes),
      middle_notes: parseNotes(sub.middle_notes),
      base_notes: parseNotes(sub.base_notes),
      description: sub.description || '',
    });
  };

  const saveAndApproveSubmission = async () => {
    try {
      // First update the submission with the enriched data via admin fragrances endpoint
      // We pass the form data along with the approve call
      await axios.put(`${API}/api/submissions/${editingSubmission.id}/approve`, subForm, { headers });
      setSubmissions(prev => prev.filter(s => s.id !== editingSubmission.id));
      setEditingSubmission(null);
      setSubForm({});
      setSubMsg('Approved — user notified by email ✓');
      setTimeout(() => setSubMsg(''), 4000);
    } catch (err) { setSubMsg(err.response?.data?.error || 'Failed to approve'); }
  };

  const rejectSubmission = async () => {
    try {
      await axios.put(`${API}/api/submissions/${rejectModal.id}/reject`, { reason: rejectReason || null }, { headers });
      setSubmissions(prev => prev.filter(s => s.id !== rejectModal.id));
      setRejectModal(null);
      setRejectReason('');
      setSubMsg('Rejected — user notified by email');
      setTimeout(() => setSubMsg(''), 4000);
    } catch (err) { setSubMsg(err.response?.data?.error || 'Failed to reject'); }
  };

  const saveUnverified = async () => {
    try {
      await axios.patch(`${API}/api/admin/fragrances/${editingUnverified.id}`, unverifiedForm, { headers });
      setUnverifiedMsg('Saved');
      setEditingUnverified(null);
      setUnverifiedForm({});
      fetchUnverified();
      setTimeout(() => setUnverifiedMsg(''), 3000);
    } catch (err) { setUnverifiedMsg(err.response?.data?.error || 'Failed to save'); }
  };

  const markVerified = async (id) => {
    try {
      await axios.patch(`${API}/api/admin/fragrances/${id}`, { verified: true }, { headers });
      setUnverified(prev => prev.filter(f => f.id !== id));
      setUnverifiedMsg('Marked as verified');
      if (editingUnverified?.id === id) { setEditingUnverified(null); setUnverifiedForm({}); }
      setTimeout(() => setUnverifiedMsg(''), 3000);
    } catch (err) { setUnverifiedMsg('Failed'); }
  };

  const saveAndVerify = async () => {
    try {
      await axios.patch(`${API}/api/admin/fragrances/${editingUnverified.id}`, { ...unverifiedForm, verified: true }, { headers });
      setUnverified(prev => prev.filter(f => f.id !== editingUnverified.id));
      setEditingUnverified(null);
      setUnverifiedForm({});
      setUnverifiedMsg('Saved & verified ✓');
      setTimeout(() => setUnverifiedMsg(''), 3000);
    } catch (err) { setUnverifiedMsg(err.response?.data?.error || 'Failed'); }
  };

  const openEditUnverified = (frag) => {
    setEditingUnverified(frag);
    const parseNotes = (n) => { if (!n) return ''; if (Array.isArray(n)) return n.join(', '); try { return JSON.parse(n).join(', '); } catch { return n; } };
    setUnverifiedForm({
      ...frag,
      top_notes: parseNotes(frag.top_notes),
      middle_notes: parseNotes(frag.middle_notes),
      base_notes: parseNotes(frag.base_notes),
      season: Array.isArray(frag.season) ? frag.season.join(', ') : (frag.season || ''),
    });
  };

  const setUField = (field) => (e) => setUnverifiedForm(f => ({ ...f, [field]: e.target.value }));

  const saveFrag = async () => {
    try {
      if (editingFrag) {
        await axios.patch(`${API}/api/admin/fragrances/${editingFrag.id}`, fragForm, { headers });
        setFragMsg('Fragrance updated');
      } else {
        await axios.post(`${API}/api/admin/fragrances`, fragForm, { headers });
        setFragMsg('Fragrance added');
      }
      setEditingFrag(null);
      setShowAddFrag(false);
      setFragForm({});
      fetchFragrances();
      setTimeout(() => setFragMsg(''), 3000);
    } catch (err) { setFragMsg(err.response?.data?.error || 'Failed to save'); }
  };

  const deleteFrag = (id) => {
    setConfirmModal({
      message: 'Delete this fragrance? This cannot be undone.',
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/api/admin/fragrances/${id}`, { headers });
          fetchFragrances();
          setUnverified(prev => prev.filter(f => f.id !== id));
          setFragMsg('Fragrance deleted');
          setTimeout(() => setFragMsg(''), 3000);
        } catch { setFragMsg('Failed to delete'); }
        setConfirmModal(null);
      }
    });
  };

  const saveUser = async () => {
    try {
      await axios.patch(`${API}/api/admin/users/${editingUser.id}`, {
        username: editingUser.username,
        email: editingUser.email,
        is_admin: editingUser.is_admin,
        is_public: editingUser.is_public,
      }, { headers });
      setUserMsg('User updated');
      setEditingUser(null);
      fetchUsers();
      setTimeout(() => setUserMsg(''), 3000);
    } catch (err) { setUserMsg(err.response?.data?.error || 'Failed to update'); }
  };

  const deleteUser = (id, username) => {
    setConfirmModal({
      message: `Delete ${username} and all their data? This cannot be undone.`,
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/api/admin/users/${id}`, { headers });
          fetchUsers();
          setUserMsg('User deleted');
          setTimeout(() => setUserMsg(''), 3000);
        } catch { setUserMsg('Failed to delete'); }
        setConfirmModal(null);
      }
    });
  };

  const deleteEntry = (id) => {
    setConfirmModal({
      message: 'Delete this journal entry?',
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/api/admin/journal/${id}`, { headers });
          fetchEntries();
          setEntryMsg('Entry deleted');
          setTimeout(() => setEntryMsg(''), 3000);
        } catch { setEntryMsg('Failed to delete'); }
        setConfirmModal(null);
      }
    });
  };

  const openEditFrag = (frag) => {
    setEditingFrag(frag);
    setShowAddFrag(false);
    const parseNotes = (n) => {
      if (!n) return '';
      if (Array.isArray(n)) return n.join(', ');
      try { return JSON.parse(n).join(', '); } catch { return n; }
    };
    setFragForm({
      ...frag,
      top_notes: parseNotes(frag.top_notes),
      middle_notes: parseNotes(frag.middle_notes),
      base_notes: parseNotes(frag.base_notes),
      season: Array.isArray(frag.season) ? frag.season.join(', ') : (frag.season || ''),
    });
  };

  const openAddFrag = () => {
    setEditingFrag(null);
    setShowAddFrag(true);
    setFragForm({ name: '', brand: '', type: '', gender: '', main_accord: '', top_notes: '', middle_notes: '', base_notes: '', longevity: '', sillage: '', price_gbp: '', year_released: '', season: '', occasion: '', description: '' });
  };

  const setField = (field) => (e) => setFragForm(f => ({ ...f, [field]: e.target.value }));

  const maskEmail = (email) => {
    if (!email) return '—';
    const [local, domain] = email.split('@');
    const visible = local.slice(0, 2);
    return `${visible}***@${domain}`;
  };

  if (!user?.is_admin) return null;

  return (
    <div style={s.page}>
      <div style={s.container}>
        <p style={s.eyebrow}>Admin</p>
        <h1 style={s.pageTitle}>Admin Panel</h1>

        <div style={s.tabs}>
          {['overview', 'fragrances', 'unverified', 'submissions', 'users', 'journal'].map(tab => (
            <button key={tab} style={s.tab(activeTab === tab)} onClick={() => setActiveTab(tab)}>
              {tab}
              {tab === 'submissions' && submissions.length > 0 && activeTab !== 'submissions' && (
                <span style={{ marginLeft: '6px', background: '#c9a96e', color: '#0a0a0a', fontSize: '0.5rem', padding: '1px 5px', borderRadius: '2px' }}>
                  {submissions.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* ── Overview ── */}
        {activeTab === 'overview' && stats && (
          <div>
            <div style={s.statsGrid}>
              <div style={s.statCard}><div style={s.statVal}>{stats.total_users}</div><div style={s.statLabel}>Total Users</div></div>
              <div style={s.statCard}><div style={s.statVal}>{stats.total_fragrances}</div><div style={s.statLabel}>Fragrances</div></div>
              <div style={s.statCard}><div style={s.statVal}>{stats.total_journal_entries}</div><div style={s.statLabel}>Journal Entries</div></div>
              <div style={s.statCard}><div style={s.statVal}>{stats.total_library_entries}</div><div style={s.statLabel}>Library Entries</div></div>
            </div>
            <div style={{ ...s.statsGrid, gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', marginTop: '1px' }}>
              <div style={s.statCard}><div style={s.statVal}>{stats.verified_users}</div><div style={s.statLabel}>Verified Users</div></div>
              <div style={s.statCard}><div style={s.statVal}>{stats.public_profiles}</div><div style={s.statLabel}>Public Profiles</div></div>
              <div style={s.statCard}><div style={s.statVal}>{stats.new_users_7d}</div><div style={s.statLabel}>New (7 days)</div></div>
              <div style={s.statCard}><div style={s.statVal}>{stats.new_users_30d}</div><div style={s.statLabel}>New (30 days)</div></div>
            </div>
            {stats.top_fragrances?.length > 0 && (
              <div style={{ marginTop: '40px' }}>
                <p style={{ fontSize: '0.62rem', letterSpacing: '0.3em', textTransform: 'uppercase', color: '#3a3a3a', marginBottom: '16px' }}>Most Collected Fragrances</p>
                <table style={s.table}>
                  <thead><tr><th style={s.th}>Fragrance</th><th style={s.th}>Brand</th><th style={s.th}>In Libraries</th></tr></thead>
                  <tbody>
                    {stats.top_fragrances.map(f => (
                      <tr key={f.id}>
                        <td style={s.tdName}>{f.name}</td>
                        <td style={s.td}>{f.brand}</td>
                        <td style={s.td}>{f.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── Submissions ── */}
        {activeTab === 'submissions' && (
          <div>
            {subMsg && <p style={subMsg.includes('ailed') ? s.errorMsg : s.successMsg}>{subMsg}</p>}
            <p style={{ fontSize: '0.7rem', color: '#3a3a3a', marginBottom: '24px', letterSpacing: '0.05em' }}>
              {submissions.length} pending submission{submissions.length !== 1 ? 's' : ''}
            </p>

            {/* ── Edit & Approve form ── */}
            {editingSubmission && (
              <div style={s.formPanel}>
                <p style={s.formTitle}>Review & Approve: {editingSubmission.brand} {editingSubmission.name}</p>
                <p style={{ fontSize: '0.7rem', color: '#3a3a3a', marginBottom: '24px', letterSpacing: '0.05em' }}>
                  Fill in as much detail as possible before approving. Notes and accord are the most important.
                </p>
                <div style={s.formGrid}>
                  <div style={s.formGroup}><label style={s.formLabel}>Name</label><input style={s.formInput} value={subForm.name || ''} onChange={e => setSubForm(f => ({ ...f, name: e.target.value }))} /></div>
                  <div style={s.formGroup}><label style={s.formLabel}>Brand</label><input style={s.formInput} value={subForm.brand || ''} onChange={e => setSubForm(f => ({ ...f, brand: e.target.value }))} /></div>
                  <div style={s.formGroup}><label style={s.formLabel}>Type</label><select style={s.formSelect} value={subForm.type || ''} onChange={e => setSubForm(f => ({ ...f, type: e.target.value }))}><option value=''>Select...</option><option>Eau de Parfum</option><option>Eau de Toilette</option><option>Parfum</option><option>Eau de Cologne</option><option>Extrait de Parfum</option></select></div>
                  <div style={s.formGroup}><label style={s.formLabel}>Gender</label><select style={s.formSelect} value={subForm.gender || ''} onChange={e => setSubForm(f => ({ ...f, gender: e.target.value }))}><option value=''>Select...</option><option>Male</option><option>Female</option><option>Unisex</option></select></div>
                  <div style={s.formGroup}><label style={s.formLabel}>Main Accord *</label><input style={s.formInput} value={subForm.main_accord || ''} onChange={e => setSubForm(f => ({ ...f, main_accord: e.target.value }))} placeholder="e.g. Woody, Oriental" /></div>
                  <div style={s.formGroup}><label style={s.formLabel}>Price (£)</label><input style={s.formInput} type='number' value={subForm.price_gbp || ''} onChange={e => setSubForm(f => ({ ...f, price_gbp: e.target.value }))} /></div>
                  <div style={s.formGroup}><label style={s.formLabel}>Year Released</label><input style={s.formInput} type='number' value={subForm.year_released || ''} onChange={e => setSubForm(f => ({ ...f, year_released: e.target.value }))} /></div>
                  <div style={s.formGroup}><label style={s.formLabel}>Longevity (1-5)</label><input style={s.formInput} type='number' min='1' max='5' value={subForm.longevity || ''} onChange={e => setSubForm(f => ({ ...f, longevity: e.target.value }))} /></div>
                  <div style={s.formGroup}><label style={s.formLabel}>Sillage (1-5)</label><input style={s.formInput} type='number' min='1' max='5' value={subForm.sillage || ''} onChange={e => setSubForm(f => ({ ...f, sillage: e.target.value }))} /></div>
                  <div style={s.formGroup}><label style={s.formLabel}>Season</label><input style={s.formInput} value={subForm.season || ''} onChange={e => setSubForm(f => ({ ...f, season: e.target.value }))} placeholder='Spring, Summer, Autumn, Winter' /></div>
                  <div style={s.formGroup}><label style={s.formLabel}>Occasion</label><input style={s.formInput} value={subForm.occasion || ''} onChange={e => setSubForm(f => ({ ...f, occasion: e.target.value }))} /></div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '12px', marginBottom: '20px' }}>
                  <div style={s.formGroup}><label style={s.formLabel}>Top Notes *</label><input style={s.formInput} value={subForm.top_notes || ''} onChange={e => setSubForm(f => ({ ...f, top_notes: e.target.value }))} placeholder='Bergamot, Pepper...' /></div>
                  <div style={s.formGroup}><label style={s.formLabel}>Middle Notes *</label><input style={s.formInput} value={subForm.middle_notes || ''} onChange={e => setSubForm(f => ({ ...f, middle_notes: e.target.value }))} placeholder='Lavender, Cedar...' /></div>
                  <div style={s.formGroup}><label style={s.formLabel}>Base Notes *</label><input style={s.formInput} value={subForm.base_notes || ''} onChange={e => setSubForm(f => ({ ...f, base_notes: e.target.value }))} placeholder='Sandalwood, Musk...' /></div>
                  <div style={s.formGroup}><label style={s.formLabel}>Description</label><textarea style={{ ...s.formInput, height: '80px', resize: 'vertical' }} value={subForm.description || ''} onChange={e => setSubForm(f => ({ ...f, description: e.target.value }))} /></div>
                </div>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  <button style={{ ...s.btn, background: '#4a7a4a' }} onClick={saveAndApproveSubmission}>✓ Save & Approve</button>
                  <button style={s.outlineBtn} onClick={() => { setEditingSubmission(null); setSubForm({}); }}>Cancel</button>
                </div>
              </div>
            )}

            {subLoading ? (
              <p style={{ color: '#3a3a3a', fontSize: '0.75rem', fontStyle: 'italic' }}>Loading...</p>
            ) : submissions.length === 0 && !editingSubmission ? (
              <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.2rem', color: '#3a3a3a', fontStyle: 'italic' }}>No pending submissions</p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={s.table}>
                  <thead>
                    <tr>
                      <th style={s.th}>Fragrance</th>
                      <th style={s.th}>Brand</th>
                      <th style={s.th}>Submitted By</th>
                      <th style={s.th}>Type</th>
                      <th style={s.th}>Notes</th>
                      <th style={s.th}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {submissions.map(sub => (
                      <tr key={sub.id} style={{ background: editingSubmission?.id === sub.id ? '#111111' : 'transparent' }}>
                        <td style={s.tdName}>{sub.name}</td>
                        <td style={s.td}>{sub.brand}</td>
                        <td style={s.td}>{sub.submitted_by_username}</td>
                        <td style={s.td}>{sub.type || '—'}</td>
                        <td style={s.td}>
                          {(sub.top_notes?.length || sub.middle_notes?.length || sub.base_notes?.length)
                            ? <span style={{ color: '#7aad7a', fontSize: '0.6rem', letterSpacing: '0.1em' }}>Has notes</span>
                            : <span style={{ color: '#7a3030', fontSize: '0.6rem', letterSpacing: '0.1em' }}>Missing</span>}
                        </td>
                        <td style={{ ...s.td, display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          <button style={s.approveBtn} onClick={() => openEditSubmission(sub)}>Edit & Approve</button>
                          <button style={s.dangerBtn} onClick={() => { setRejectModal({ id: sub.id, name: sub.name, brand: sub.brand }); setRejectReason(''); }}>✕ Reject</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── Fragrances ── */}
        {activeTab === 'fragrances' && (
          <div>
            {fragMsg && <p style={fragMsg.includes('ailed') ? s.errorMsg : s.successMsg}>{fragMsg}</p>}
            <p style={{ fontSize: '0.7rem', color: '#3a3a3a', marginBottom: '20px', letterSpacing: '0.05em' }}>
              Showing verified fragrances only. Edit notes here after verifying — use the <span style={{ color: '#c9a96e' }}>Unverified</span> tab to work through new ones.
            </p>
            <div style={s.searchRow}>
              <input style={s.input} placeholder="Search fragrances..." value={fragSearch} onChange={e => {
                setFragSearch(e.target.value);
                clearTimeout(fragSearchTimer.current);
                fragSearchTimer.current = setTimeout(() => fetchFragrances(e.target.value), 300);
              }} />
              <button style={s.outlineBtn} onClick={() => fetchFragrances(fragSearch)}>Search</button>
              <button style={s.btn} onClick={openAddFrag}>+ Add Fragrance</button>
            </div>

            {(showAddFrag || editingFrag) && (
              <div style={s.formPanel}>
                <p style={s.formTitle}>{editingFrag ? `Editing: ${editingFrag.name}` : 'Add New Fragrance'}</p>
                <div style={s.formGrid}>
                  <div style={s.formGroup}><label style={s.formLabel}>Name *</label><input style={s.formInput} value={fragForm.name || ''} onChange={setField('name')} placeholder="Fragrance name" /></div>
                  <div style={s.formGroup}><label style={s.formLabel}>Brand *</label><input style={s.formInput} value={fragForm.brand || ''} onChange={setField('brand')} placeholder="Brand name" /></div>
                  <div style={s.formGroup}><label style={s.formLabel}>Type</label><select style={s.formSelect} value={fragForm.type || ''} onChange={setField('type')}><option value="">Select...</option><option>Eau de Parfum</option><option>Eau de Toilette</option><option>Parfum</option><option>Eau de Cologne</option><option>Eau Fraiche</option><option>Extrait de Parfum</option><option>Cologne Absolue</option><option>Cologne Intense</option></select></div>
                  <div style={s.formGroup}><label style={s.formLabel}>Gender</label><select style={s.formSelect} value={fragForm.gender || ''} onChange={setField('gender')}><option value="">Select...</option><option>Male</option><option>Female</option><option>Unisex</option></select></div>
                  <div style={s.formGroup}><label style={s.formLabel}>Main Accord</label><input style={s.formInput} value={fragForm.main_accord || ''} onChange={setField('main_accord')} placeholder="e.g. Woody, Citrus" /></div>
                  <div style={s.formGroup}><label style={s.formLabel}>Price (£)</label><input style={s.formInput} type="number" value={fragForm.price_gbp || ''} onChange={setField('price_gbp')} placeholder="0.00" /></div>
                  <div style={s.formGroup}><label style={s.formLabel}>Year Released</label><input style={s.formInput} type="number" value={fragForm.year_released || ''} onChange={setField('year_released')} placeholder="e.g. 2021" /></div>
                  <div style={s.formGroup}><label style={s.formLabel}>Longevity (1–5)</label><input style={s.formInput} type="number" min="1" max="5" value={fragForm.longevity || ''} onChange={setField('longevity')} /></div>
                  <div style={s.formGroup}><label style={s.formLabel}>Sillage (1–5)</label><input style={s.formInput} type="number" min="1" max="5" value={fragForm.sillage || ''} onChange={setField('sillage')} /></div>
                  <div style={s.formGroup}><label style={s.formLabel}>Season</label><input style={s.formInput} value={fragForm.season || ''} onChange={setField('season')} placeholder="Spring, Summer, Autumn, Winter" /></div>
                  <div style={s.formGroup}><label style={s.formLabel}>Occasion</label><input style={s.formInput} value={fragForm.occasion || ''} onChange={setField('occasion')} placeholder="e.g. Daily, Evening" /></div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '12px', marginBottom: '20px' }}>
                  <div style={s.formGroup}><label style={s.formLabel}>Top Notes</label><input style={s.formInput} value={fragForm.top_notes || ''} onChange={setField('top_notes')} placeholder="Bergamot, Lemon, Pepper" /></div>
                  <div style={s.formGroup}><label style={s.formLabel}>Middle Notes</label><input style={s.formInput} value={fragForm.middle_notes || ''} onChange={setField('middle_notes')} placeholder="Rose, Jasmine, Cedar" /></div>
                  <div style={s.formGroup}><label style={s.formLabel}>Base Notes</label><input style={s.formInput} value={fragForm.base_notes || ''} onChange={setField('base_notes')} placeholder="Sandalwood, Musk, Vanilla" /></div>
                  <div style={s.formGroup}><label style={s.formLabel}>Description</label><textarea style={{ ...s.formInput, height: '80px', resize: 'vertical' }} value={fragForm.description || ''} onChange={setField('description')} placeholder="Short description..." /></div>
                </div>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  <button style={s.btn} onClick={saveFrag}>Save Fragrance</button>
                  <button style={s.outlineBtn} onClick={() => { setEditingFrag(null); setShowAddFrag(false); setFragForm({}); }}>Cancel</button>
                </div>
              </div>
            )}

            {fragLoading ? (
              <p style={{ color: '#3a3a3a', fontSize: '0.75rem', fontStyle: 'italic' }}>Loading...</p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={s.table}>
                  <thead><tr><th style={s.th}>Name</th><th style={s.th}>Brand</th><th style={s.th}>Type</th><th style={s.th}>Gender</th><th style={s.th}>Accord</th><th style={s.th}>Price</th><th style={s.th}>Actions</th></tr></thead>
                  <tbody>
                    {fragrances.map(f => (
                      <tr key={f.id} style={{ background: editingFrag?.id === f.id ? '#111111' : 'transparent' }}>
                        <td style={s.tdName}>{f.name}</td>
                        <td style={s.td}>{f.brand}</td>
                        <td style={s.td}>{f.type}</td>
                        <td style={s.td}>{f.gender}</td>
                        <td style={s.td}>{f.main_accord}</td>
                        <td style={s.td}>{f.price_gbp ? `£${f.price_gbp}` : '—'}</td>
                        <td style={{ ...s.td, display: 'flex', gap: '8px' }}>
                          <button style={s.outlineBtn} onClick={() => openEditFrag(f)}>Edit</button>
                          <button style={s.dangerBtn} onClick={() => deleteFrag(f.id)}>Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p style={{ fontSize: '0.62rem', color: '#2a2a2a', marginTop: '12px', letterSpacing: '0.1em' }}>{fragrances.length} results</p>
              </div>
            )}
          </div>
        )}

        {/* ── Unverified ── */}
        {activeTab === 'unverified' && (
          <div>
            {unverifiedMsg && <p style={unverifiedMsg.includes('ailed') ? s.errorMsg : s.successMsg}>{unverifiedMsg}</p>}
            <p style={{ fontSize: '0.7rem', color: '#3a3a3a', marginBottom: '24px', letterSpacing: '0.05em' }}>{unverified.length} fragrances remaining to verify</p>
            {editingUnverified && (
              <div style={s.formPanel}>
                <p style={s.formTitle}>Editing: {editingUnverified.name}</p>
                <div style={s.formGrid}>
                  <div style={s.formGroup}><label style={s.formLabel}>Name</label><input style={s.formInput} value={unverifiedForm.name || ''} onChange={setUField('name')} /></div>
                  <div style={s.formGroup}><label style={s.formLabel}>Brand</label><input style={s.formInput} value={unverifiedForm.brand || ''} onChange={setUField('brand')} /></div>
                  <div style={s.formGroup}><label style={s.formLabel}>Type</label><select style={s.formSelect} value={unverifiedForm.type || ''} onChange={setUField('type')}><option value=''>Select...</option><option>Eau de Parfum</option><option>Eau de Toilette</option><option>Parfum</option><option>Eau de Cologne</option><option>Extrait de Parfum</option></select></div>
                  <div style={s.formGroup}><label style={s.formLabel}>Gender</label><select style={s.formSelect} value={unverifiedForm.gender || ''} onChange={setUField('gender')}><option value=''>Select...</option><option>Male</option><option>Female</option><option>Unisex</option></select></div>
                  <div style={s.formGroup}><label style={s.formLabel}>Main Accord</label><input style={s.formInput} value={unverifiedForm.main_accord || ''} onChange={setUField('main_accord')} /></div>
                  <div style={s.formGroup}><label style={s.formLabel}>Price (£)</label><input style={s.formInput} type='number' value={unverifiedForm.price_gbp || ''} onChange={setUField('price_gbp')} /></div>
                  <div style={s.formGroup}><label style={s.formLabel}>Year Released</label><input style={s.formInput} type='number' value={unverifiedForm.year_released || ''} onChange={setUField('year_released')} /></div>
                  <div style={s.formGroup}><label style={s.formLabel}>Longevity (1-5)</label><input style={s.formInput} type='number' min='1' max='5' value={unverifiedForm.longevity || ''} onChange={setUField('longevity')} /></div>
                  <div style={s.formGroup}><label style={s.formLabel}>Sillage (1-5)</label><input style={s.formInput} type='number' min='1' max='5' value={unverifiedForm.sillage || ''} onChange={setUField('sillage')} /></div>
                  <div style={s.formGroup}><label style={s.formLabel}>Season</label><input style={s.formInput} value={unverifiedForm.season || ''} onChange={setUField('season')} placeholder='Spring, Summer, Autumn, Winter' /></div>
                  <div style={s.formGroup}><label style={s.formLabel}>Occasion</label><input style={s.formInput} value={unverifiedForm.occasion || ''} onChange={setUField('occasion')} /></div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '12px', marginBottom: '20px' }}>
                  <div style={s.formGroup}><label style={s.formLabel}>Top Notes</label><input style={s.formInput} value={unverifiedForm.top_notes || ''} onChange={setUField('top_notes')} /></div>
                  <div style={s.formGroup}><label style={s.formLabel}>Middle Notes</label><input style={s.formInput} value={unverifiedForm.middle_notes || ''} onChange={setUField('middle_notes')} /></div>
                  <div style={s.formGroup}><label style={s.formLabel}>Base Notes</label><input style={s.formInput} value={unverifiedForm.base_notes || ''} onChange={setUField('base_notes')} /></div>
                  <div style={s.formGroup}><label style={s.formLabel}>Description</label><textarea style={{ ...s.formInput, height: '80px', resize: 'vertical' }} value={unverifiedForm.description || ''} onChange={setUField('description')} /></div>
                </div>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  <button style={s.btn} onClick={saveUnverified}>Save Changes</button>
                  <button style={{ ...s.btn, background: '#4a7a4a' }} onClick={saveAndVerify}>✓ Save & Verify</button>
                  <button style={s.outlineBtn} onClick={() => { setEditingUnverified(null); setUnverifiedForm({}); }}>Cancel</button>
                </div>
              </div>
            )}
            {unverifiedLoading ? (
              <p style={{ color: '#3a3a3a', fontSize: '0.75rem', fontStyle: 'italic' }}>Loading...</p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={s.table}>
                  <thead><tr><th style={s.th}>Name</th><th style={s.th}>Brand</th><th style={s.th}>Type</th><th style={s.th}>Notes</th><th style={s.th}>Actions</th></tr></thead>
                  <tbody>
                    {unverified.map(f => (
                      <tr key={f.id} style={{ background: editingUnverified?.id === f.id ? '#111111' : 'transparent' }}>
                        <td style={s.tdName}>{f.name}</td>
                        <td style={s.td}>{f.brand}</td>
                        <td style={s.td}>{f.type || '—'}</td>
                        <td style={s.td}>
                          {(f.top_notes?.length || f.middle_notes?.length || f.base_notes?.length)
                            ? <span style={{ color: '#7aad7a', fontSize: '0.6rem', letterSpacing: '0.1em' }}>Has notes</span>
                            : <span style={{ color: '#7a3030', fontSize: '0.6rem', letterSpacing: '0.1em' }}>Missing</span>}
                        </td>
                        <td style={{ ...s.td, display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          <button style={s.outlineBtn} onClick={() => openEditUnverified(f)}>Edit</button>
                          <button style={{ ...s.outlineBtn, borderColor: '#4a7a4a', color: '#7aad7a' }} onClick={() => markVerified(f.id)}>✓ Verify</button>
                          <button style={s.dangerBtn} onClick={() => deleteFrag(f.id)}>Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── Users ── */}
        {activeTab === 'users' && (
          <div>
            {userMsg && <p style={userMsg.includes('ailed') ? s.errorMsg : s.successMsg}>{userMsg}</p>}
            <div style={s.searchRow}>
              <input style={s.input} placeholder="Search users..." value={userSearch} onChange={e => {
                setUserSearch(e.target.value);
                clearTimeout(userSearchTimer.current);
                userSearchTimer.current = setTimeout(() => fetchUsers(e.target.value), 300);
              }} />
              <button style={s.outlineBtn} onClick={() => fetchUsers(userSearch)}>Search</button>
            </div>
            {editingUser && (
              <div style={{ ...s.formPanel, marginBottom: '24px' }}>
                <p style={s.formTitle}>Editing: {editingUser.username}</p>
                <div style={s.formGrid}>
                  <div style={s.formGroup}><label style={s.formLabel}>Username</label><input style={s.formInput} value={editingUser.username} onChange={e => setEditingUser(u => ({ ...u, username: e.target.value }))} /></div>
                  <div style={s.formGroup}><label style={s.formLabel}>Email</label><input style={s.formInput} value={editingUser.email} onChange={e => setEditingUser(u => ({ ...u, email: e.target.value }))} /></div>
                  <div style={s.formGroup}><label style={s.formLabel}>Admin</label><select style={s.formSelect} value={editingUser.is_admin ? 'true' : 'false'} onChange={e => setEditingUser(u => ({ ...u, is_admin: e.target.value === 'true' }))}><option value="false">No</option><option value="true">Yes</option></select></div>
                  <div style={s.formGroup}><label style={s.formLabel}>Public Profile</label><select style={s.formSelect} value={editingUser.is_public ? 'true' : 'false'} onChange={e => setEditingUser(u => ({ ...u, is_public: e.target.value === 'true' }))}><option value="false">No</option><option value="true">Yes</option></select></div>
                </div>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button style={s.btn} onClick={saveUser}>Save User</button>
                  <button style={s.outlineBtn} onClick={() => setEditingUser(null)}>Cancel</button>
                </div>
              </div>
            )}
            {userLoading ? (
              <p style={{ color: '#3a3a3a', fontSize: '0.75rem', fontStyle: 'italic' }}>Loading...</p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={s.table}>
                  <thead><tr><th style={s.th}>Username</th><th style={s.th}>Email</th><th style={s.th}>Joined</th><th style={s.th}>Status</th><th style={s.th}>Library</th><th style={s.th}>Actions</th></tr></thead>
                  <tbody>
                    {users.map(u => (
                      <tr key={u.id}>
                        <td style={s.tdName}>{u.username}{u.is_admin && <span style={{ ...s.badge('#c9a96e'), marginLeft: '8px' }}>Admin</span>}</td>
                        <td style={s.td}>{maskEmail(u.email)}</td>
                        <td style={s.td}>{new Date(u.created_at).toLocaleDateString('en-GB')}</td>
                        <td style={s.td}><span style={s.badge(u.email_verified ? '#7aad7a' : '#6b6460')}>{u.email_verified ? 'Verified' : 'Unverified'}</span></td>
                        <td style={s.td}>{u.library_count || 0}</td>
                        <td style={{ ...s.td, display: 'flex', gap: '8px' }}>
                          <button style={s.outlineBtn} onClick={() => setEditingUser(u)}>Edit</button>
                          {u.id !== user.id && <button style={s.dangerBtn} onClick={() => deleteUser(u.id, u.username)}>Delete</button>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p style={{ fontSize: '0.62rem', color: '#2a2a2a', marginTop: '12px', letterSpacing: '0.1em' }}>{users.length} users</p>
              </div>
            )}
          </div>
        )}

        {/* ── Journal ── */}
        {activeTab === 'journal' && (
          <div>
            {entryMsg && <p style={entryMsg.includes('ailed') ? s.errorMsg : s.successMsg}>{entryMsg}</p>}
            {entryLoading ? (
              <p style={{ color: '#3a3a3a', fontSize: '0.75rem', fontStyle: 'italic' }}>Loading...</p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={s.table}>
                  <thead><tr><th style={s.th}>User</th><th style={s.th}>Fragrance</th><th style={s.th}>Date</th><th style={s.th}>Occasion</th><th style={s.th}>Notes</th><th style={s.th}>Actions</th></tr></thead>
                  <tbody>
                    {entries.map(e => (
                      <tr key={e.id}>
                        <td style={s.td}>{e.username}</td>
                        <td style={s.tdName}>{e.fragrance_brand} {e.fragrance_name}</td>
                        <td style={s.td}>{new Date(e.date_worn).toLocaleDateString('en-GB')}</td>
                        <td style={s.td}>{e.occasion || '—'}</td>
                        <td style={{ ...s.td, maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.notes || '—'}</td>
                        <td style={s.td}><button style={s.dangerBtn} onClick={() => deleteEntry(e.id)}>Delete</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p style={{ fontSize: '0.62rem', color: '#2a2a2a', marginTop: '12px', letterSpacing: '0.1em' }}>{entries.length} entries</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Reject Modal ── */}
      {rejectModal && (
        <div style={s.overlay} onClick={() => setRejectModal(null)}>
          <div style={{ ...s.modal, maxWidth: '480px', textAlign: 'left' }} onClick={e => e.stopPropagation()}>
            <p style={{ ...s.modalTitle, textAlign: 'center' }}>Reject Submission</p>
            <p style={{ ...s.modalMsg, textAlign: 'center' }}>
              Rejecting <strong style={{ color: '#f5f0eb' }}>{rejectModal.brand} {rejectModal.name}</strong><br />
              The user will be notified by email.
            </p>
            <div style={s.formGroup}>
              <label style={s.formLabel}>Reason (optional)</label>
              <textarea
                style={{ ...s.formInput, height: '80px', resize: 'vertical', marginTop: '6px' }}
                placeholder="e.g. Duplicate entry, insufficient information, not a fragrance..."
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
              />
            </div>
            <div style={{ ...s.modalBtns, marginTop: '24px' }}>
              <button style={s.dangerBtn} onClick={rejectSubmission}>Confirm Reject</button>
              <button style={s.outlineBtn} onClick={() => setRejectModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Confirm Modal ── */}
      {confirmModal && (
        <div style={s.overlay} onClick={() => setConfirmModal(null)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <p style={s.modalTitle}>Are you sure?</p>
            <p style={s.modalMsg}>{confirmModal.message}</p>
            <div style={s.modalBtns}>
              <button style={s.dangerBtn} onClick={confirmModal.onConfirm}>Confirm Delete</button>
              <button style={s.outlineBtn} onClick={() => setConfirmModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;